import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Network, Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Floating pill navbar — pattern 21st.dev / Magic
 * shadcn Button for CTA · active:scale-[0.96] · 40px hit areas
 */
export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [pillShape, setPillShape] = useState('rounded-full');
  const shapeRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (shapeRef.current) clearTimeout(shapeRef.current);
    if (isMobileMenuOpen) {
      setPillShape('rounded-2xl');
    } else {
      shapeRef.current = setTimeout(() => setPillShape('rounded-full'), 300);
    }
    return () => clearTimeout(shapeRef.current);
  }, [isMobileMenuOpen]);

  const navLinks = [
    { href: '#hero', label: 'Accueil' },
    { href: '#portfolio', label: 'Portfolio' },
    { href: '#expertise', label: 'Expertise' },
    { href: '#contact', label: 'Contact' },
  ];

  return (
    <motion.header
      data-testid="header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={`
        fixed top-5 left-0 right-0 mx-auto z-50
        w-[calc(100%-2rem)] max-w-4xl
        transition-[border-color,box-shadow] duration-300
        ${pillShape}
        border border-[#4facfe]/20
        bg-[#04081a]/75 backdrop-blur-2xl
        shadow-[0_0_0_1px_rgba(79,172,254,0.06),0_8px_40px_rgba(0,0,0,0.55)]
        ${isScrolled ? 'border-[#4facfe]/30 shadow-[0_0_0_1px_rgba(79,172,254,0.12),0_12px_48px_rgba(0,0,0,0.65)]' : ''}
      `}
    >
      {/* Top inner glow line */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#4facfe]/30 to-transparent rounded-full pointer-events-none" />

      <div className="px-5 py-3">
        <div className="flex items-center justify-between gap-6">

          {/* Logo */}
          <motion.a
            href="#hero"
            data-testid="logo"
            className="flex items-center gap-2 group flex-shrink-0"
            whileHover={{ scale: 1.03 }}
          >
            <div className="relative">
              <Network className="w-7 h-7 text-[#4facfe] group-hover:text-[#00f2fe] transition-colors duration-300" />
              <div className="absolute inset-0 bg-[#4facfe]/25 blur-md rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
            <span className="font-outfit text-lg font-bold tracking-tight">
              <span className="text-white">TECH</span>
              <span className="text-gradient">NEXUS</span>
            </span>
          </motion.a>

          {/* Desktop nav — 21st AnimatedNavLink flip */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                /* make-interfaces-feel-better: min 40px hit area, specific transition */
                className="group relative px-4 rounded-full hover:bg-white/[0.06] overflow-hidden inline-flex items-center h-10 transition-[background-color] duration-200"
              >
                <div className="flex flex-col transition-transform duration-300 ease-out group-hover:-translate-y-1/2">
                  <span className="text-white/60 font-inter text-sm font-medium leading-none pb-[0.45rem]">
                    {link.label}
                  </span>
                  <span className="text-white font-inter text-sm font-medium leading-none pt-[0.45rem]">
                    {link.label}
                  </span>
                </div>
              </a>
            ))}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-3 flex-shrink-0">
            {/* CTA — shadcn Button with gradient override + scale-on-press */}
            <Button
              data-testid="cta-header-button"
              asChild
              className="hidden md:inline-flex bg-gradient-to-r from-[#00f2fe] to-[#4facfe] text-[#030a1a] font-semibold rounded-full px-5 h-9 text-sm border-0 shadow-none animate-glow-pulse hover:opacity-90 hover:bg-none active:scale-[0.96] transition-[transform,opacity] duration-150"
            >
              <a href="#contact">Commencer</a>
            </Button>

            {/* Mobile toggle — make-interfaces-feel-better: min 44×44 hit area */}
            <motion.button
              data-testid="mobile-menu-button"
              className="md:hidden w-11 h-11 flex items-center justify-center text-white/70 hover:text-white transition-colors rounded-full hover:bg-white/[0.08]"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              whileTap={{ scale: 0.96 }}
            >
              {isMobileMenuOpen
                ? <X className="w-5 h-5" />
                : <Menu className="w-5 h-5" />
              }
            </motion.button>
          </div>
        </div>

        {/* Mobile dropdown */}
        <motion.div
          initial={false}
          animate={{
            height: isMobileMenuOpen ? 'auto' : 0,
            opacity: isMobileMenuOpen ? 1 : 0,
          }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="md:hidden overflow-hidden"
        >
          <nav className="pt-3 pb-2 mt-2 border-t border-white/[0.06] flex flex-col gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                /* make-interfaces-feel-better: min 44px touch target */
                className="block text-white/65 hover:text-white text-sm font-medium py-3 px-3 rounded-xl hover:bg-white/[0.05] transition-[background-color,color] duration-150 min-h-[44px] flex items-center"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <div className="pt-2">
              {/* shadcn Button + scale on press */}
              <Button
                data-testid="cta-mobile-button"
                asChild
                className="w-full bg-gradient-to-r from-[#00f2fe] to-[#4facfe] text-[#030a1a] font-semibold rounded-xl px-5 h-12 text-sm border-0 shadow-[0_0_20px_rgba(79,172,254,0.35)] hover:opacity-90 active:scale-[0.96] transition-[transform,opacity] duration-150"
              >
                <a href="#contact" onClick={() => setIsMobileMenuOpen(false)}>Commencer</a>
              </Button>
            </div>
          </nav>
        </motion.div>
      </div>
    </motion.header>
  );
};

export default Header;
